// apps/provider/src/stores/calendarStore.js
// Route-persistent calendar data store: appointments + class sessions keyed by
// id, with covered date-range tracking and stale-while-revalidate fetching.
// Survives AppointmentCalendar unmounts (back-from-chart renders instantly)
// and is the single patch target for mutations and realtime events.
//
// Identity rule: components must subscribe to the stable references
// (appointmentsById, classSessionsById, coveredRanges) and derive arrays in
// their own useMemo — store selectors must never return fresh arrays.

import { create } from 'zustand';
import { apiService } from '../services/api';
import { useCalendarAlertStore } from './calendarAlertStore';

let rangeAbortController = null;

// ---------- date-string helpers (YYYY-MM-DD, lexicographic-safe) ----------

const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addDays = (dateStr, n) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return fmtDate(new Date(y, m - 1, d + n));
};

// Walk sorted ranges from `start`; covered when the chain reaches `end` with no gap.
// Returns the oldest fetchedAt among the covering ranges for staleness checks.
function rangeCoverage(ranges, start, end) {
  const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start));
  let cursor = start;
  let oldest = Infinity;
  for (const r of sorted) {
    if (r.end < cursor) continue;
    if (r.start > cursor) break;
    oldest = Math.min(oldest, r.fetchedAt);
    if (r.end >= end) return { covered: true, oldestFetchedAt: oldest };
    cursor = addDays(r.end, 1);
  }
  return { covered: false, oldestFetchedAt: null };
}

// Subtract [start, end] from existing ranges, then insert it with the new
// fetchedAt — keeps per-range freshness accurate instead of inheriting the
// oldest constituent on merge.
function upsertRange(ranges, start, end, fetchedAt) {
  const out = [];
  for (const r of ranges) {
    if (r.end < start || r.start > end) {
      out.push(r);
      continue;
    }
    if (r.start < start) out.push({ start: r.start, end: addDays(start, -1), fetchedAt: r.fetchedAt });
    if (r.end > end) out.push({ start: addDays(end, 1), end: r.end, fetchedAt: r.fetchedAt });
  }
  out.push({ start, end, fetchedAt });
  return out.sort((a, b) => a.start.localeCompare(b.start));
}

const buildFiltersKey = (filters = {}) =>
  JSON.stringify([filters.location_id || null, filters.status || '', filters.appointment_type || '']);

// ---------- module-level bookkeeping (not reactive state) ----------

// id -> timestamp of last local/realtime touch; guards reconcile-removal
const touchedAt = new Map();
// id -> timestamp of deletion; guards stale in-flight fetches re-adding deleted records
const tombstones = new Map();
// in-flight fetch dedupe: key -> Promise
const inFlight = new Map();
// realtime ingest debounce
let ingestQueue = new Set();
let ingestTimer = null;
let burstRevalidateTimer = null;
// A burst refetch that came due while the tab was hidden; released on visible
let burstPendingHidden = false;
// per-appointment timers for the transient "recently updated" pulse highlight
const pulseTimers = new Map();

const REVALIDATE_MIN_AGE_MS = 60000;
// 3s window: bursty writes to the same records (auto-save-ish flows were
// observed PATCHing one appointment 7x/min) collapse into one flush instead
// of fanning out per event. Realtime creates appear in ~3s — still live.
const INGEST_DEBOUNCE_MS = 3000;
// Random spread added to the debounce. Every tab in an org receives the same
// realtime event at the same instant and runs the same fixed debounce, so a
// clinic with 40 open calendars fetched the same appointment 40 times in the
// same second (2026-09-22 request history: 45/s bursts, pool stalls of 10-40s).
// The upsert also re-runs the alert effect, so spreading the ingest spreads the
// alert refetch wave that follows it.
const INGEST_JITTER_MAX_MS = 5000;
// Singles are ~3KB each vs the ~1MB range-revalidation fallback — reserve the
// fallback for true bursts (batch wizard), not moderately busy windows
const INGEST_SINGLE_FETCH_MAX = 10;
// A burst over INGEST_SINGLE_FETCH_MAX falls back to one full-range refetch
// (~1MB). Every visible tab in the org crosses the threshold at the same
// moment (a batch create publishes one event per slot), so that refetch is
// spread a little; hidden tabs never reach it (they hold). Kept short so the
// user who just ran the batch wizard sees their own appointments promptly.
const BURST_REVALIDATE_JITTER_MAX_MS = 10000;
// Ids queued while the tab was hidden flush shortly after it becomes visible;
// users focus tabs at their own moments, this just avoids a same-second wave
// at shift change.
const INGEST_VISIBLE_FLUSH_JITTER_MAX_MS = 3000;
const EVICTION_RECORD_LIMIT = 15000;
const PULSE_DURATION_MS = 3000;

const initialState = {
  appointmentsById: {},
  classSessionsById: {},
  coveredRanges: [],
  filtersKey: null,
  activeWaitlistEntries: [],
  waitlistFetchedAt: null,
  isFetching: false,
  lastError: null,
  // Params of the most recent fetchRange call — the visible range; used by
  // no-arg revalidateRange() from mutation handlers and realtime bursts.
  lastFetchParams: null,
  // Appointment ids touched by realtime in the last few seconds — drives the
  // green pulse highlight on any surface rendering from the store
  recentlyUpdatedIds: new Set(),
};

const normalizeClassSession = (session) => ({
  ...session,
  event_type: 'class_session',
  appointment_date: session.session_date,
  duration_minutes: session.class_definition?.duration_minutes || 60,
  class_color: session.class_definition?.color,
  location: session.location?.name || '',
  _location: session.location || null,
});

export const useCalendarStore = create((set, get) => ({
  ...initialState,

  // ---------- non-reactive helpers ----------

  isRangeCovered: (start, end, { maxAgeMs } = {}) => {
    const { covered, oldestFetchedAt } = rangeCoverage(get().coveredRanges, start, end);
    if (!covered) return false;
    if (maxAgeMs != null && Date.now() - oldestFetchedAt > maxAgeMs) return false;
    return true;
  },

  isDateCovered: (dateStr) => {
    if (!dateStr) return false;
    return rangeCoverage(get().coveredRanges, dateStr, dateStr).covered;
  },

  getAppointmentsInRange: (start, end) => {
    const { appointmentsById, classSessionsById } = get();
    const inRange = (apt) => apt.appointment_date >= start && apt.appointment_date <= end;
    return [
      ...Object.values(appointmentsById).filter(inRange),
      ...Object.values(classSessionsById).filter(inRange),
    ];
  },

  // ---------- fetching ----------

  // params: { start, end, filters: {location_id, status, appointment_type},
  //           usesClasses, usesWaitlist, organizationId }
  // opts: { background, force } — background fetches never flip isFetching and,
  // unless forced, are skipped when the range was fetched < REVALIDATE_MIN_AGE_MS
  // ago. A server-side filter change clears the store and forces a foreground
  // fetch regardless of what the caller requested.
  fetchRange: async (params, { background = false, force = false } = {}) => {
    const { start, end, filters = {}, usesClasses, usesWaitlist, organizationId } = params;
    if (!start || !end || !organizationId) return;

    const filtersKey = buildFiltersKey(filters);
    if (get().filtersKey !== null && get().filtersKey !== filtersKey) {
      get().invalidateAll();
      background = false;
    }

    if (background && !force) {
      const { covered, oldestFetchedAt } = rangeCoverage(get().coveredRanges, start, end);
      if (covered && Date.now() - oldestFetchedAt < REVALIDATE_MIN_AGE_MS) return;
    }

    const flightKey = `${start}|${end}|${filtersKey}`;
    if (inFlight.has(flightKey)) return inFlight.get(flightKey);

    const fetchStartedAt = Date.now();
    if (!background) {
      if (rangeAbortController) rangeAbortController.abort();
      rangeAbortController = new AbortController();
      set({ isFetching: true, lastError: null });
    }
    const signal = !background ? rangeAbortController?.signal : undefined;
    set({ filtersKey, lastFetchParams: params });

    const run = (async () => {
      try {
        const [appointments, classSessions, waitlistEntries] = await Promise.all([
          apiService.getCalendarAppointments({
            start_date: start,
            end_date: end,
            location_id: filters.location_id || undefined,
            status: filters.status || undefined,
            appointment_type: filters.appointment_type || undefined,
          }, { signal }),
          usesClasses
            ? apiService.getClassSessions({
                organization_id: organizationId,
                start_date: start,
                end_date: end,
                ...(filters.location_id ? { location_id: filters.location_id } : {}),
              }).catch((err) => {
                console.error('calendarStore: class sessions fetch failed:', err);
                return null;
              })
            : Promise.resolve(null),
          usesWaitlist
            ? apiService.getAllWaitlistEntries({ status: 'active' }).catch((err) => {
                console.error('calendarStore: waitlist fetch failed:', err);
                return null;
              })
            : Promise.resolve(null),
        ]);

        get()._applyRangeResult({
          start,
          end,
          fetchStartedAt,
          appointments: Array.isArray(appointments) ? appointments : [],
          classSessions: classSessions == null ? null
            : (Array.isArray(classSessions) ? classSessions : []),
          waitlistEntries: waitlistEntries == null ? null : waitlistEntries,
        });
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('calendarStore: fetchRange failed:', error);
        set({ lastError: error });
      } finally {
        inFlight.delete(flightKey);
        if (!background) set({ isFetching: false });
      }
    })();

    inFlight.set(flightKey, run);
    return run;
  },

  // Background refresh of the most recently requested (visible) range.
  // Always forced — callers (mutations, reconnect, realtime bursts) need
  // certainty; the freshness skip only applies to navigation revalidation.
  revalidateRange: (overrides = {}) => {
    const params = get().lastFetchParams;
    if (!params) return;
    return get().fetchRange({ ...params, ...overrides }, { background: true, force: true });
  },

  _applyRangeResult: ({ start, end, fetchStartedAt, appointments, classSessions, waitlistEntries }) => {
    set((state) => {
      const nextAppointments = { ...state.appointmentsById };
      const responseIds = new Set();

      for (const apt of appointments) {
        if (apt?.id == null) continue;
        responseIds.add(apt.id);
        if ((tombstones.get(apt.id) || 0) > fetchStartedAt) continue;
        const existing = nextAppointments[apt.id];
        if (existing?.updated_at && apt.updated_at && apt.updated_at < existing.updated_at) continue;
        nextAppointments[apt.id] = existing ? { ...existing, ...apt } : apt;
      }

      // Reconcile: drop records in the fetched window that the server no longer
      // returns, unless something local touched them after the fetch started.
      for (const [id, apt] of Object.entries(nextAppointments)) {
        if (apt.appointment_date < start || apt.appointment_date > end) continue;
        if (responseIds.has(apt.id)) continue;
        if ((touchedAt.get(apt.id) || 0) > fetchStartedAt) continue;
        delete nextAppointments[id];
      }

      let nextClassSessions = state.classSessionsById;
      if (classSessions !== null) {
        nextClassSessions = { ...state.classSessionsById };
        const classIds = new Set();
        for (const session of classSessions) {
          if (session?.id == null) continue;
          classIds.add(session.id);
          nextClassSessions[session.id] = normalizeClassSession(session);
        }
        for (const [id, cs] of Object.entries(nextClassSessions)) {
          if (cs.appointment_date < start || cs.appointment_date > end) continue;
          if (!classIds.has(cs.id)) delete nextClassSessions[id];
        }
      }

      const next = {
        appointmentsById: nextAppointments,
        classSessionsById: nextClassSessions,
        coveredRanges: upsertRange(state.coveredRanges, start, end, fetchStartedAt),
        // A successful apply clears any earlier failure (background fetches
        // never reset lastError on start, so it would otherwise stick).
        lastError: null,
      };
      if (waitlistEntries !== null) {
        next.activeWaitlistEntries = waitlistEntries;
        next.waitlistFetchedAt = fetchStartedAt;
      }
      return next;
    });
    get()._evictIfOversized(start);
  },

  // Drop covered ranges farthest from the anchor date (and their exclusive
  // records) when the store grows past the limit — bounds long-session memory.
  _evictIfOversized: (anchorStart) => {
    const state = get();
    const count = Object.keys(state.appointmentsById).length + Object.keys(state.classSessionsById).length;
    if (count <= EVICTION_RECORD_LIMIT || state.coveredRanges.length <= 1) return;

    const byDistance = [...state.coveredRanges].sort((a, b) => {
      const da = Math.abs(new Date(a.start) - new Date(anchorStart));
      const db = Math.abs(new Date(b.start) - new Date(anchorStart));
      return db - da; // farthest first
    });
    const dropped = byDistance[0];
    const remaining = state.coveredRanges.filter((r) => r !== dropped);

    const stillCovered = (dateStr) => rangeCoverage(remaining, dateStr, dateStr).covered;
    const prune = (map) => {
      const next = {};
      for (const [id, rec] of Object.entries(map)) {
        const inDropped = rec.appointment_date >= dropped.start && rec.appointment_date <= dropped.end;
        if (inDropped && !stillCovered(rec.appointment_date)) continue;
        next[id] = rec;
      }
      return next;
    };

    set({
      appointmentsById: prune(state.appointmentsById),
      classSessionsById: prune(state.classSessionsById),
      coveredRanges: remaining,
    });
    // Recurse until under the limit or one range left
    get()._evictIfOversized(anchorStart);
  },

  // ---------- record-level mutations ----------

  // Full stitched records only (from getCalendarAppointments). Guarded so an
  // older record never overwrites a newer one (mutation echoes are no-ops).
  upsertAppointments: (records) => {
    const list = (Array.isArray(records) ? records : [records]).filter((r) => r?.id != null);
    if (!list.length) return;
    const now = Date.now();
    set((state) => {
      const next = { ...state.appointmentsById };
      for (const rec of list) {
        if (tombstones.has(rec.id)) continue;
        const existing = next[rec.id];
        if (existing?.updated_at && rec.updated_at && rec.updated_at < existing.updated_at) continue;
        next[rec.id] = existing ? { ...existing, ...rec } : rec;
        touchedAt.set(rec.id, now);
      }
      return { appointmentsById: next };
    });
    // An appointment changed → its case/patient alert facts may have too
    const alertStore = useCalendarAlertStore.getState();
    for (const rec of list) {
      alertStore.invalidateForAppointment({ caseId: rec.case_id, patientId: rec.patient_id });
    }
  },

  // Partial merge for known records (bare mutation responses, realtime status
  // payloads). Never replaces stitched nested objects — only the given fields.
  patchAppointment: (id, fields) => {
    if (id == null || !fields) return;
    const existing = get().appointmentsById[id];
    if (!existing) return;
    touchedAt.set(id, Date.now());
    set((state) => ({
      appointmentsById: {
        ...state.appointmentsById,
        [id]: { ...state.appointmentsById[id], ...fields },
      },
    }));
    // Status/schedule changes move visit counts and auth consumption — refresh
    // this appointment's alert facts (surgical: one case + one patient)
    useCalendarAlertStore.getState().invalidateForAppointment({
      caseId: existing.case_id,
      patientId: existing.patient_id,
    });
  },

  // Transient highlight for realtime-touched appointments (3s green pulse)
  markRecentlyUpdated: (id) => {
    if (id == null) return;
    set((state) => {
      const next = new Set(state.recentlyUpdatedIds);
      next.add(id);
      return { recentlyUpdatedIds: next };
    });
    if (pulseTimers.has(id)) clearTimeout(pulseTimers.get(id));
    pulseTimers.set(id, setTimeout(() => {
      pulseTimers.delete(id);
      set((state) => {
        if (!state.recentlyUpdatedIds.has(id)) return {};
        const next = new Set(state.recentlyUpdatedIds);
        next.delete(id);
        return { recentlyUpdatedIds: next };
      });
    }, PULSE_DURATION_MS));
  },

  removeAppointment: (id) => {
    if (id == null) return;
    const now = Date.now();
    tombstones.set(id, now);
    touchedAt.set(id, now);
    set((state) => {
      if (!(id in state.appointmentsById)) return {};
      const next = { ...state.appointmentsById };
      delete next[id];
      return { appointmentsById: next };
    });
  },

  // ---------- realtime ingest (hybrid: single fetches, burst -> revalidate) ----------

  // Queue created/updated appointment ids from realtime events. Debounced:
  // few ids -> parallel single stitched fetches; a burst -> one background
  // range revalidation instead. Hidden tabs only queue (2026-09-22): nothing
  // on screen needs the record, and 40 hidden calendars fetching the same
  // appointment at once was the 9/22 outage. useCalendarRealtimeSync flushes
  // or drops the queue when the tab becomes visible.
  ingestAppointmentIds: (ids) => {
    for (const id of Array.isArray(ids) ? ids : [ids]) {
      if (id != null) ingestQueue.add(id);
    }
    if (ingestTimer) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    get()._scheduleIngestFlush(INGEST_DEBOUNCE_MS + Math.random() * INGEST_JITTER_MAX_MS);
  },

  pendingIngestCount: () => ingestQueue.size,

  // A burst refetch came due while hidden: the visibility backstop turns it
  // into one range revalidate (and clears it via dropIngestQueue).
  hasPendingBurst: () => burstPendingHidden,

  // Tab became visible after a short absence: fetch what was queued.
  flushIngestQueue: () => {
    if (ingestQueue.size === 0 || ingestTimer) return;
    get()._scheduleIngestFlush(Math.random() * INGEST_VISIBLE_FLUSH_JITTER_MAX_MS);
  },

  // Tab became visible after a long absence: the caller revalidates the
  // range instead, so the queued singles would be redundant.
  dropIngestQueue: () => {
    ingestQueue = new Set();
    burstPendingHidden = false;
  },

  _scheduleBurstRevalidate: () => {
    if (burstRevalidateTimer) return;
    burstRevalidateTimer = setTimeout(() => {
      burstRevalidateTimer = null;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        burstPendingHidden = true;
        return;
      }
      get().revalidateRange();
    }, Math.random() * BURST_REVALIDATE_JITTER_MAX_MS);
  },

  _scheduleIngestFlush: (delayMs) => {
    ingestTimer = setTimeout(async () => {
      ingestTimer = null;
      // Hidden by the time the debounce fires: keep the ids queued for the
      // visibility backstop instead of fetching from a background tab
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const queued = [...ingestQueue];
      ingestQueue = new Set();
      if (!queued.length) return;

      if (queued.length > INGEST_SINGLE_FETCH_MAX) {
        get()._scheduleBurstRevalidate();
        return;
      }

      const filters = get().lastFetchParams?.filters || {};
      const results = await Promise.all(
        queued.map((id) =>
          apiService.getCalendarAppointments({ appointment_id: id }).catch((err) => {
            console.error('calendarStore: single-appointment ingest failed:', err);
            return null;
          })
        )
      );
      results.forEach((records, i) => {
        if (records === null) return;
        const rec = Array.isArray(records) ? records[0] : null;
        if (!rec) {
          // Server no longer returns it — treat as gone
          get().removeAppointment(queued[i]);
          return;
        }
        // Respect the active location filter — don't surface records a
        // server-filtered range fetch would exclude.
        if (filters.location_id && rec.location_id && rec.location_id !== filters.location_id) return;
        get().upsertAppointments([rec]);
      });
    }, delayMs);
  },

  // ---------- lifecycle ----------

  invalidateAll: () => {
    touchedAt.clear();
    tombstones.clear();
    set({
      appointmentsById: {},
      classSessionsById: {},
      coveredRanges: [],
    });
  },

  reset: () => {
    touchedAt.clear();
    tombstones.clear();
    ingestQueue = new Set();
    if (ingestTimer) {
      clearTimeout(ingestTimer);
      ingestTimer = null;
    }
    if (burstRevalidateTimer) {
      clearTimeout(burstRevalidateTimer);
      burstRevalidateTimer = null;
    }
    burstPendingHidden = false;
    for (const timer of pulseTimers.values()) clearTimeout(timer);
    pulseTimers.clear();
    set({ ...initialState, recentlyUpdatedIds: new Set() });
  },
}));
